import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

export type DropdownMenuItem = {
    id: string;
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    className?: string;
    divider?: boolean;
}

export type DropdownMenuProps = {
    id?: string;
    items: DropdownMenuItem[];
    trigger: React.ReactNode;
    position?: 'left' | 'right';
    className?: string;
}

export const DropdownMenu = ({ id, items, trigger, position = 'right', className = '' }: DropdownMenuProps) => {
    const baseId = id || 'dropdown-menu';
    const [isOpen, setIsOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                triggerRef.current &&
                menuRef.current &&
                !triggerRef.current.contains(event.target as Node) &&
                !menuRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            updateMenuPosition();
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const updateMenuPosition = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const menuWidth = 160;
            const menuHeight = items.length * 40 + 20;

            let top = rect.bottom + 4;
            let left = position === 'right' ? rect.right - menuWidth : rect.left;

            if (top + menuHeight > window.innerHeight) {
                top = rect.top - menuHeight - 4;
            }

            if (left + menuWidth > window.innerWidth) {
                left = window.innerWidth - menuWidth - 10;
            }

            if (left < 10) {
                left = 10;
            }

            setMenuPosition({ top, left });
        }
    };

    const handleTriggerClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsOpen(!isOpen);
    };

    const handleItemClick = (item: DropdownMenuItem) => {
        item.onClick();
        setIsOpen(false);
    };

    const menuPortal = isOpen && ReactDOM.createPortal(
        <div
            id={`${baseId}-menu`}
            data-testid={`${baseId}-menu`}
            ref={menuRef}
            className={`tw-fixed tw-bg-surface-raised tw-rounded tw-shadow-ds-md tw-border tw-border-edge tw-py-1 tw-z-[9999] tw-min-w-[160px]`}
            style={{
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
            }}
        >
            <div id={`${baseId}-items`}>
            {items.map((item, index) => (
                <React.Fragment key={item.id}>
                    {item.divider && index > 0 && (
                        <div id={`${baseId}-divider-${index}`} className="tw-border-t tw-border-edge tw-my-1" />
                    )}
                    <button
                        id={`${baseId}-item-${index}`}
                        data-testid={`${baseId}-item-${index}`}
                        onClick={() => handleItemClick(item)}
                        className={`tw-w-full tw-px-4 tw-py-2 tw-text-left tw-text-sm tw-text-ink hover:tw-bg-surface-hover tw-flex tw-items-center tw-gap-2 ${
                            item.className || ''
                        }`}
                    >
                        {item.icon && <span id={`${baseId}-item-${index}-icon`} className="tw-flex-shrink-0 icon-follow-ink">{item.icon}</span>}
                        <span id={`${baseId}-item-${index}-label`} className='tw-text-xs'>{item.label}</span>
                    </button>
                </React.Fragment>
            ))}
            </div>
        </div>,
        document.body
    );

    return (
        <>
            <div id={baseId} data-testid={baseId} className={`tw-relative ${className}`} ref={triggerRef}>
                <div role="button" data-testid="common-dropdownmenu-div-1" id={`${baseId}-trigger`} onClick={handleTriggerClick} className="tw-cursor-pointer">
                    {trigger}
                </div>
            </div>
            {menuPortal}
        </>
    );
};
